/* Hans product presentation. Shopify/Impact owns variant selection, cart and checkout. */
(() => {
  const order = ['a', 'fit', 'b', 'c', 'd'];
  const labels = { a: 'Three-quarter view', fit: 'Worn view', b: 'Front view', c: 'Side · shields off', d: 'Side · shields on' };
  const skuColors = { HDH_LL24Y: 'yellow', HDH_LL24W: 'white', HDH_LL24G: 'slate', HDH_LL24B: 'black' };

  function colorFor(variant) {
    const sku = String(variant?.sku || '').trim().toUpperCase();
    if (skuColors[sku]) return skuColors[sku];
    const name = String(variant?.title || '').toLowerCase();
    if (/snow bunny|yellow/.test(name)) return 'yellow';
    if (/blizzard|white/.test(name)) return 'white';
    if (/mountain monocles|slate/.test(name)) return 'slate';
    if (/mogul|black/.test(name)) return 'black';
    return 'unknown';
  }

  function imageUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, document.baseURI);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
    } catch { return null; }
  }

  function variantImage(variant) {
    return imageUrl(variant?.featured_image?.src || variant?.featured_media?.preview_image?.src);
  }

  function setImage(img, src, alt, role) {
    if (!img) return;
    img.parentElement?.querySelectorAll('source').forEach(source => source.removeAttribute('srcset'));
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.hidden = !src;
    img.alt = alt || '';
    if (!src) {
      delete img.dataset.hansSource;
      img.removeAttribute('src');
      return;
    }
    img.dataset.hansSource = src;
    const zoom = img.hasAttribute('data-zoom-image');
    const thumb = role === 'thumbnail';
    if (zoom || thumb) img.loading = 'lazy';
    if (img.classList.contains('hx-gallery-image')) img.setAttribute('fetchpriority', 'high');
    let displaySrc = src;
    try {
      const url = new URL(src, document.baseURI);
      const shopifyImage = /\/cdn\/shop\/files\/|\/s\/files\//.test(url.pathname);
      if (shopifyImage && !zoom) {
        const sourceWidth = Number(url.searchParams.get('width')) || 1600;
        const limit = Math.min(sourceWidth, thumb ? 240 : 1600);
        const widths = [...new Set((thumb ? [96, 160, 240] : [320, 480, 640, 800, 1200, 1600]).filter(width => width <= limit).concat(limit))];
        img.setAttribute('srcset', widths.map(width => {
          const candidate = new URL(url.href);
          candidate.searchParams.set('width', String(width));
          return `${candidate.href} ${width}w`;
        }).join(', '));
        img.setAttribute('sizes', thumb ? '80px' : img.classList.contains('hx-gallery-image') ? '(min-width: 1000px) 55vw, 100vw' : '(min-width: 1000px) 50vw, 100vw');
        if (thumb) {
          url.searchParams.set('width', String(limit));
          displaySrc = url.href;
        }
      }
    } catch { /* Unrecognized URLs keep their original source. */ }
    if (img.getAttribute('src') !== displaySrc) img.src = displaySrc;
  }

  class HansGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      try { this.config = JSON.parse(this.querySelector('[data-gallery-config]').textContent); } catch { return; }
      this.failed = new Set();
      this.mobileQuery = window.matchMedia?.('(max-width: 749px)');
      this.mobileQuery?.addEventListener('change', () => this.render(), options);
      this.variant = this.config.initialVariant || {};
      this.color = this.variant.id ? colorFor(this.variant) : this.dataset.initialColor;
      this.name = this.variant.title || this.dataset.initialName || this.config.productTitle || 'Legacy Lenses';
      this.view = 'a';
      this.addEventListener('click', event => this.click(event), options);
      this.addEventListener('error', event => {
        if (!(event.target instanceof HTMLImageElement)) return;
        const src = imageUrl(event.target.dataset.hansSource || event.target.currentSrc || event.target.getAttribute('src'));
        if (!src || this.failed.has(src)) return;
        this.failed.add(src);
        this.render();
      }, { ...options, capture: true });
      document.addEventListener('hans:media-error', event => {
        if (String(event.detail?.productId) !== this.dataset.productId || event.detail?.formId !== this.dataset.form) return;
        const src = imageUrl(event.detail.src);
        if (!src || this.failed.has(src)) return;
        this.failed.add(src); this.render();
      }, options);
      document.addEventListener('variant:change', event => {
        if (event.detail?.formId !== this.dataset.form || !event.detail.variant) return;
        this.variant = event.detail.variant;
        this.color = colorFor(this.variant);
        this.name = this.variant.title || this.config.productTitle || 'Legacy Lenses';
        this.render();
      }, options);
      const dialog = this.querySelector('dialog');
      dialog?.addEventListener('click', event => {
        if (event.target === dialog) this.closeZoom();
      }, options);
      dialog?.addEventListener('close', () => {
        if (this.returnFocus?.isConnected && !this.returnFocus.disabled) this.returnFocus.focus();
        this.returnFocus = null;
      }, options);
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.querySelector('dialog')?.close();
    }

    validImage(...candidates) {
      for (const candidate of candidates) {
        const src = imageUrl(candidate);
        if (src && !this.failed.has(src)) return src;
      }
      return null;
    }

    media() {
      return {
        productId: this.dataset.productId,
        formId: this.dataset.form,
        variantId: this.variant?.id,
        color: this.color,
        name: this.name,
        views: this.views,
        label: this.config.colors?.[this.color]?.label || this.name,
        lensMirror: this.config.colors?.[this.color]?.lensMirror || '',
        lensTint: this.config.colors?.[this.color]?.lensTint || '',
        labels: this.config.labels || labels
      };
    }

    render() {
      const entry = this.config.colors?.[this.color];
      // The initial native image belongs to that initial selection only. Never carry it into another color.
      const initialImage = String(this.variant?.id) === String(this.config.initialVariant?.id) ? this.config.nativeImage : null;
      const nativeImage = this.validImage(variantImage(this.variant), this.color === 'unknown' ? initialImage : null);
      this.views = {};
      this.nativeView = false;
      for (const view of order) {
        const src = this.validImage(this.mobileQuery?.matches ? entry?.mobileViews?.[view] : null, entry?.views?.[view], entry?.fallbacks?.[view]);
        if (src) this.views[view] = src;
      }
      if (!this.views.a && nativeImage) {
        this.views.a = nativeImage;
        this.nativeView = true;
      }
      this.available = order.filter(view => this.views[view]);
      if (!this.available.includes(this.view)) this.view = this.available[0] || null;
      const thumbs = this.querySelector('.hx-thumbs');
      if (thumbs) {
        thumbs.replaceChildren();
        this.available.forEach(view => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.galleryView = view;
          button.setAttribute('aria-label', `${this.name} — ${this.viewLabel(view)}`);
          button.setAttribute('aria-pressed', String(view === this.view));
          const img = document.createElement('img');
          setImage(img, this.views[view], '', 'thumbnail'); img.alt = ''; img.width = 120; img.height = 120; img.loading = 'lazy';
          button.append(img); thumbs.append(button);
        });
      }
      this.showView();
      document.dispatchEvent(new CustomEvent('hans:media-change', { detail: this.media() }));
    }

    viewLabel(view) { return view === 'a' && this.nativeView ? 'Product photo' : this.config.labels?.[view] || labels[view] || 'Product photo'; }

    showView() {
      const src = this.views?.[this.view] || null;
      const alt = `${this.name} — ${this.viewLabel(this.view)}`;
      const stage = this.querySelector('.hx-gallery-stage');
      if (stage) stage.dataset.view = this.view || '';
      setImage(this.querySelector('.hx-gallery-image'), src, alt);
      setImage(this.querySelector('[data-zoom-image]'), src, alt);
      const caption = this.querySelector('.hx-gallery-caption');
      if (caption) caption.textContent = src ? `${this.name} · ${this.viewLabel(this.view)}` : `${this.name} · Photo unavailable`;
      const zoomCaption = this.querySelector('[data-zoom-caption]');
      if (zoomCaption) zoomCaption.textContent = src ? alt : 'Photo unavailable';
      const zoomButton = this.querySelector('[data-gallery-zoom]');
      if (zoomButton) zoomButton.disabled = !src;
      this.querySelectorAll('[data-gallery-empty]').forEach(el => { el.hidden = Boolean(src); });
      this.querySelectorAll('.hx-zoom-hint').forEach(el => { el.hidden = !src; });
      this.querySelectorAll('[data-gallery-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.galleryView === this.view)));
      this.querySelectorAll('[data-zoom-next], [data-zoom-prev]').forEach(button => { button.disabled = this.available.length < 2; });
      if (!src) this.closeZoom();
    }

    click(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.dataset.galleryView && this.available.includes(button.dataset.galleryView)) {
        this.view = button.dataset.galleryView; this.showView();
      }
      if (button.hasAttribute('data-gallery-zoom') && this.views[this.view]) {
        const dialog = this.querySelector('dialog');
        if (dialog && !dialog.open) { this.returnFocus = button; dialog.showModal(); }
      }
      if (button.hasAttribute('data-close-zoom')) this.closeZoom();
      if ((button.hasAttribute('data-zoom-next') || button.hasAttribute('data-zoom-prev')) && this.available.length) {
        const delta = button.hasAttribute('data-zoom-next') ? 1 : -1;
        this.view = this.available[(this.available.indexOf(this.view) + delta + this.available.length) % this.available.length];
        this.showView();
      }
    }

    closeZoom() { const dialog = this.querySelector('dialog'); if (dialog?.open) dialog.close(); }
  }

  class HansDetails extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.failed = new Set();
      this.shield = 'd';
      this.mobileQuery = window.matchMedia?.('(max-width: 749px)');
      this.mobileQuery?.addEventListener('change', () => {
        if (!this.gallery() && this.variant) this.updateVariant(this.variant);
      }, options);
      try { this.fallbackColors = JSON.parse(this.querySelector('[data-details-config]').textContent); } catch { this.fallbackColors = {}; }
      this.loadMediaConfig();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-shield]');
        if (!button || !this.contains(button) || button.disabled) return;
        this.shield = button.dataset.shield; this.update(this.media);
      }, options);
      this.addEventListener('error', event => {
        if (!(event.target instanceof HTMLImageElement) || !event.target.matches('[data-shields-image], [data-fit-image]')) return;
        const src = imageUrl(event.target.dataset.hansSource || event.target.currentSrc || event.target.getAttribute('src'));
        if (!src || this.failed.has(src)) return;
        this.failed.add(src);
        if (event.target.matches('[data-shields-image]') && this.shieldColors) {
          this.update(this.media);
          return;
        }
        if (this.gallery()) {
          document.dispatchEvent(new CustomEvent('hans:media-error', { detail: { productId: this.dataset.productId, formId: this.formId(), src } }));
          this.update(this.gallery().media());
        } else if (this.variant) this.updateVariant(this.variant);
        else this.update(this.media);
      }, { ...options, capture: true });
      document.addEventListener('hans:media-change', event => {
        if (String(event.detail?.productId) !== this.dataset.productId || event.detail?.formId !== this.formId()) return;
        this.update(event.detail);
      }, options);
      document.addEventListener('variant:change', event => {
        if (!event.detail?.variant || event.detail.formId !== this.formId() || this.gallery()) return;
        this.updateVariant(event.detail.variant);
      }, options);
      document.addEventListener('shopify:section:load', () => {
        this.loadMediaConfig();
        const gallery = this.gallery();
        if (gallery?.config) this.update(gallery.media());
        else {
          const picker = Array.from(document.querySelectorAll('variant-picker')).find(el => el.getAttribute('form-id') === this.formId());
          try { this.updateVariant(JSON.parse(picker.querySelector('[data-variant]').textContent)); } catch { /* A removed main section has no current selection. */ }
        }
      }, options);
      const gallery = this.gallery();
      if (gallery?.config) this.update(gallery.media());
      else {
        const picker = Array.from(document.querySelectorAll('variant-picker')).find(el => el.getAttribute('form-id') === this.formId());
        try { this.updateVariant(JSON.parse(picker.querySelector('[data-variant]').textContent)); } catch { /* The server-rendered selection remains usable. */ }
      }
    }

    disconnectedCallback() { this.controller?.abort(); }

    loadMediaConfig() {
      const sourceConfig = Array.from(document.querySelectorAll('[data-hans-product-media]')).find(el => el.dataset.productId === this.dataset.productId && el.dataset.form === this.formId());
      try {
        const config = JSON.parse(sourceConfig.textContent);
        this.fallbackColors = config.colors || this.fallbackColors;
        this.labels = config.labels || labels;
      } catch { this.labels = this.labels || labels; }
    }

    formId() {
      if (this.dataset.form) return this.dataset.form;
      const prefix = `product-form-${this.dataset.productId}-`;
      const mainForm = Array.from(document.querySelectorAll('.shopify-section--main-product form[id]')).find(form => (form.getAttribute('id') || '').startsWith(prefix));
      return mainForm?.getAttribute('id') || Array.from(document.forms).find(form => (form.getAttribute('id') || '').startsWith(prefix))?.getAttribute('id');
    }

    gallery() {
      return Array.from(document.querySelectorAll('hans-gallery')).find(gallery => gallery.dataset.productId === this.dataset.productId && gallery.dataset.form === this.formId());
    }

    updateVariant(variant) {
      if (!variant) return;
      this.variant = variant;
      const color = colorFor(variant), entry = this.fallbackColors[color], views = {};
      for (const view of order) {
        const candidates = [this.mobileQuery?.matches ? entry?.mobileViews?.[view] : null, entry?.views?.[view], entry?.fallbacks?.[view]];
        const src = candidates.map(imageUrl).find(value => value && !this.failed.has(value));
        if (src) views[view] = src;
      }
      this.update({ color, name: variant.title || 'Legacy Lenses', views, label: entry?.label || variant.title, lensMirror: entry?.lensMirror || '', lensTint: entry?.lensTint || '', labels: this.labels });
    }

    update(media) {
      if (!media) return;
      this.media = media;
      const views = {};
      for (const [view, value] of Object.entries(media.views || {})) {
        const src = imageUrl(value);
        if (src && !this.failed.has(src)) views[view] = src;
      }
      // The comparison owns its photo sources; the gallery supplies only the selected color.
      try { this.shieldColors = JSON.parse(this.querySelector('[data-shields-config]').textContent); } catch { this.shieldColors = null; }
      if (this.shieldColors) {
        const entry = this.shieldColors[media.color];
        for (const view of ['c', 'd']) {
          delete views[view];
          const candidates = [this.mobileQuery?.matches ? entry?.mobileViews?.[view] : null, entry?.views?.[view], entry?.fallbacks?.[view]];
          const src = candidates.map(imageUrl).find(value => value && !this.failed.has(value));
          if (src) views[view] = src;
        }
      }
      if (!views[this.shield]) this.shield = views.d ? 'd' : views.c ? 'c' : 'd';
      this.querySelectorAll('[data-selected-name]').forEach(el => { el.textContent = media.name; });
      this.querySelectorAll('[data-selected-color]').forEach(el => { el.textContent = media.label || media.name; });
      this.querySelectorAll('[data-selected-lens-mirror]').forEach(el => { el.textContent = media.lensMirror || 'See selected product details'; });
      this.querySelectorAll('[data-selected-lens-tint]').forEach(el => { el.textContent = media.lensTint || 'See selected product details'; });
      this.querySelectorAll('[data-shields-image]').forEach(img => setImage(img, views[this.shield], `${media.name} — ${media.labels?.[this.shield] || labels[this.shield]}`));
      this.querySelectorAll('[data-shield]').forEach(button => {
        button.disabled = !views[button.dataset.shield];
        button.setAttribute('aria-pressed', String(button.dataset.shield === this.shield && !button.disabled));
      });
      this.querySelectorAll('[data-fit-image]').forEach(img => setImage(img, views.fit, `${media.name} — ${media.labels?.fit || labels.fit}`));
      this.querySelectorAll('[data-fit-missing]').forEach(el => { el.hidden = Boolean(views.fit); });
    }
  }
  if (!customElements.get('hans-gallery')) customElements.define('hans-gallery', HansGallery);
  if (!customElements.get('hans-details')) customElements.define('hans-details', HansDetails);
})();

/* Keep Shopify Inbox clear of inline purchase controls on every mobile PDP.
   Observe the native controls without changing forms, variants or app settings. */
(() => {
  const main = document.querySelector('#main');
  if (!main || !('IntersectionObserver' in window) || document.getElementById('hans-inbox-clearance')) return;
  const style = document.createElement('style');
  style.id = 'hans-inbox-clearance';
  style.textContent = '@media(max-width:999px){body[data-hans-inline-purchase-visible] > shopify-chat,body:has(product-quick-add.is-visible) > shopify-chat{display:none!important}}';
  document.head.append(style);
  const observed = new Map();
  const update = () => document.body.toggleAttribute('data-hans-inline-purchase-visible', [...observed.values()].some(Boolean));
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (observed.has(entry.target)) observed.set(entry.target, entry.isIntersecting && entry.intersectionRect.height > 0);
    }
    update();
  });
  let pending = false;
  const refresh = () => {
    pending = false;
    const controls = new Set(main.querySelectorAll('.shopify-section--main-product .product-info__buy-buttons'));
    for (const node of observed.keys()) {
      if (!controls.has(node)) { observer.unobserve(node); observed.delete(node); }
    }
    for (const node of controls) {
      if (!observed.has(node)) { observed.set(node, false); observer.observe(node); }
    }
    update();
  };
  new MutationObserver(() => {
    if (!pending) { pending = true; requestAnimationFrame(refresh); }
  }).observe(main, { childList: true, subtree: true });
  refresh();
})();
